import React from 'react';
import Type from 'prop-types';
import { getIn } from 'formik';
import {
  Chip,
  FormControl,
  FormGroup,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import { Done as DoneIcon } from '@mui/icons-material';
import styles from './ChipGroup.module.css';

const ChipGroup = (props) => {
  const {
    label: labelProp,
    type,
    options,
    required,
    field,
    form: { errors, setFieldValue },
    getOptionLabel,
    getOptionValue,
    removable,
  } = props;
  const values = field.value ? [...field.value] : [];
  const fieldError = getIn(errors, field.name);
  const showError = !!fieldError;

  const handleClick =
    ({ checkedChipIdx, chipId }) =>
    () => {
      if (checkedChipIdx > -1) {
        values.splice(checkedChipIdx, 1);
        setFieldValue(field.name, values);
      } else if (type === 'choice') {
        setFieldValue(field.name, [chipId]);
      } else {
        setFieldValue(field.name, values.concat(chipId));
      }
    };

  const getIconByType = (chipType) => {
    switch (chipType) {
      case 'filter':
        return <DoneIcon />;
      default:
        return null;
    }
  };

  const renderChip = (option) => {
    const label = getOptionLabel(option);
    const value = getOptionValue(option);
    const checkedChipIdx = field.value.indexOf(value);
    const checked = checkedChipIdx > -1;
    const Icon = checked ? getIconByType(type) : null;
    const color = checked ? 'primary' : 'default';

    const chipProps = {
      className: styles.chip,
      icon: removable ? undefined : Icon,
      onDelete:
        removable && checked
          ? handleClick({ checkedChipIdx, chipId: value })
          : undefined,
      color,
      onClick: handleClick({ checkedChipIdx, chipId: value }),
      clickable: true,
    };

    return <Chip key={value} value={value} label={label} {...chipProps} />;
  };

  return (
    <FormControl
      component="fieldset"
      margin="normal"
      error={showError}
      required={required}
    >
      <FormLabel
        sx={{
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          fontWeight: '500',
          marginBottom: '8px',
        }}
        component="div"
      >
        {labelProp}
      </FormLabel>
      <FormGroup classes={{ root: styles.chipRow }} row>
        {options.map((option) => renderChip(option))}
      </FormGroup>
      {showError && <FormHelperText>{fieldError}</FormHelperText>}
    </FormControl>
  );
};

ChipGroup.propTypes = {
  label: Type.string.isRequired,
  type: Type.oneOf(['choice', 'filter']),
  required: Type.bool,
  removable: Type.bool,
  field: Type.shape({
    name: Type.string.isRequired,
    value: Type.arrayOf(Type.oneOfType([Type.string, Type.number])).isRequired,
  }).isRequired,
  form: Type.shape({
    setFieldValue: Type.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    touched: Type.object.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    errors: Type.object,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  options: Type.arrayOf(Type.object),
  getOptionLabel: Type.func,
  getOptionValue: Type.func,
};

ChipGroup.defaultProps = {
  type: 'filter',
  options: [],
  required: false,
  removable: false,
  getOptionLabel: (option) => option.name,
  getOptionValue: (option) => option.id,
};

export default ChipGroup;
